import React, { useCallback, useState } from 'react';
import { styled } from '@compiled/react';
import { ModalBody } from '@atlaskit/modal-dialog';
import Spinner from '@atlaskit/spinner';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import type { Props } from './types.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyReleaseNotesModalContent = lazy(() =>
	import(/* webpackChunkName: "async-release-notes-modal" */ './index').then(
		(module) => module.ReleaseNotesModalContent,
	),
);

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyEmbeddedPageReleaseNotesEditorModal = lazy(() =>
	import(
		/* webpackChunkName: "async-embedded-page-release-notes-editor-modal" */ './embedded-page-release-notes-editor-modal/EmbeddedPageReleaseNotesEditorModal.tsx'
	).then((module) => module.EmbeddedPageReleaseNotesEditorModal),
);

const LoadingModalBody = () => (
	<ModalBody>
		<LoadingWrapper>
			<Spinner size="large" />
		</LoadingWrapper>
	</ModalBody>
);

export const ReleaseNotesModal = (props: Props): React.JSX.Element => {
	const [confluenceReleaseNotesDraftUrl, setConfluenceReleaseNotesDraftUrl] = useState<
		string | null
	>(null);

	const { onClose } = props;

	const onCreateConfluenceReleaseNotes = useCallback((draftUrl: string) => {
		setConfluenceReleaseNotesDraftUrl(draftUrl);
	}, []);

	const onCloseConfluenceReleaseNotes = useCallback(() => {
		setConfluenceReleaseNotesDraftUrl(null);
		onClose();
	}, [onClose]);

	return (
		<ShortcutScope>
			{confluenceReleaseNotesDraftUrl ? (
				<LazyEmbeddedPageReleaseNotesEditorModal
					draftUrl={confluenceReleaseNotesDraftUrl}
					onClose={onCloseConfluenceReleaseNotes}
				/>
			) : (
				<ModalDialog
					width="x-large"
					autoFocus={false}
					onClose={onClose}
					messageId="software-releases-add-release-notes-modal.ui.modal-dialog"
					messageType="transactional"
				>
					<Placeholder name="release-notes-modal" fallback={<LoadingModalBody />}>
						<LazyReleaseNotesModalContent
							{...props}
							onCreateConfluenceReleaseNotes={onCreateConfluenceReleaseNotes}
						/>
					</Placeholder>
				</ModalDialog>
			)}
		</ShortcutScope>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const LoadingWrapper = styled.div({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: '400px',
});
